import { Account } from '../domain/model/account.model';
import { IAccount } from '../infrastructure/interfaces/account-storage.interface';
import { AccountEntityMapper } from '../infrastructure/mappers/account-entity.mapper';
import { AccountStorageMapper } from '../infrastructure/mappers/account-storage.mapper';

/**
 * Merges accounts from server with accounts from local storage,
 * preserving local changes while incorporating server updates.
 *
 * The merge process follows these rules:
 * 1. If an account exists only on the server, add it to the result
 * 2. If an account exists only locally, preserve it completely
 * 3. If an account exists in both places (by _id):
 *    - Preserve local account fields that may have been updated locally (e.g., name, settings)
 *    - Update other properties from server (e.g., balances, timestamps)
 *
 * @param accountsFromServer - Accounts from server
 * @param accountsFromLocal - Accounts from local storage
 * @returns Merged list of accounts (as Account model instances)
 */
export function mergeAccounts(
  accountsFromServer: Account[],
  accountsFromLocal: Account[]
): Account[] {
  if (!accountsFromLocal?.length) {
    return accountsFromServer;
  }

  if (!accountsFromServer?.length) {
    return accountsFromLocal;
  }

  const localAccountsMap = new Map<string, IAccount>();

  accountsFromLocal.forEach((account) => {
    const storage = AccountStorageMapper.fromEntity(account);

    localAccountsMap.set(storage._id, storage);
  });

  const mergedAccountsStorage: IAccount[] = accountsFromServer.map(
    (serverAccount) => {
      const serverStorage = AccountStorageMapper.fromEntity(serverAccount);
      const localStorage = localAccountsMap.get(serverStorage._id);

      if (!localStorage) {
        return serverStorage;
      }

      return localStorage;
    }
  );


  return [...mergedAccountsStorage].map(
    AccountEntityMapper.fromStorage
  );
}
